<div class="page-wrapper">

  <div class="page-header">
    <div class="page-title">
      <svg class="page-title__icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="2" y="2.5" width="12" height="3" rx="1" fill="currentColor"/>
        <rect x="2" y="6.75" width="12" height="3" rx="1" fill="currentColor"/>
        <rect x="2" y="11" width="7" height="3" rx="1" fill="currentColor"/>
      </svg>
      <h1 class="page-title__text">Calls</h1>
    </div>
    <p class="page-title__sub">Audit log of every API call served across all organizations.</p>
  </div>

  @if (totals; as t) {
    <app-admin-totals-card
      [description]="totalsFilterLabel ? 'The calls matching your filters.' : 'Every call on the platform.'"
      [filterLabel]="totalsFilterLabel"
      [columns]="5"
      [loading]="isLoading"
    >
      <div appTotalsFigure label="Requests" info="How many API calls match the current filters, from the API and the Playground combined." note="Calls in this list">{{ t.requests | number }}</div>
      <div appTotalsFigure label="Errors" info="Calls that ended in an error. The note shows their share of all requests."
           [tone]="t.errors > 0 ? 'danger' : 'default'"
           [note]="(t.errorRate | percent:'1.0-1') + ' of requests'">{{ t.errors | number }}</div>
      <div appTotalsFigure label="Credits" info="Credits used by these calls." note="Charged to wallets">{{ t.credits | number }}</div>
      <div appTotalsFigure label="Cost" info="What these calls cost the organizations' wallets, including charges for paid options." note="Including paid options">{{ t.costCents | money }} {{ t.currency | uppercase }}</div>
      <div appTotalsFigure label="Avg latency" info="The average time between a request arriving and its response being sent, across these calls." note="Mean response time">{{ t.avgLatencyMs | latency }}</div>

      @if (sources.length) {
        <div totalsFooter class="source-split totals-source-split">
          <span class="source-split__label">By source</span>
          <div class="source-split__bar" role="img" [attr.aria-label]="sourcesLabel">
            @for (s of sources; track s.label) {
              <span class="source-split__segment" [style.flex-grow]="s.value" [style.background]="s.color"></span>
            }
          </div>
          <ul class="source-split__legend">
            @for (s of sources; track s.label) {
              <li>
                <span class="source-split__dot" [style.background]="s.color" aria-hidden="true"></span>
                <span class="source-split__name">{{ s.label }}</span>
                {{ s.value | number }} · {{ s.pct }}%
              </li>
            }
          </ul>
        </div>
      }
    </app-admin-totals-card>
  } @else if (!hasLoadedOnce) {
    <app-admin-totals-card [skeletonLabels]="['Requests', 'Errors', 'Credits', 'Cost', 'Avg latency']" [columns]="5" />
  }

  <div class="api-card api-card--full" #listCard>
    <div class="admin-filter-bar">
      <div class="admin-filter-bar__search">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="7.25" cy="7.25" r="4.75" stroke="currentColor" stroke-width="1.6"/><path d="M10.75 10.75 14 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
        <input type="text" placeholder="Organization (slug)..." [ngModel]="q" (ngModelChange)="onQueryChange($event)" />
      </div>
      @if (phone) {
        <!-- Phones: the other filters live in a sheet (calls-filters-sheet). -->
        <button type="button" class="admin-filter-bar__filters" (click)="openFilters()">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 4h11M4.5 8h7M6.5 12h3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
          Filters
          @if (activeFilterCount) {
            <span class="admin-filter-bar__count">{{ activeFilterCount }}</span>
          }
        </button>
      } @else {
        <input type="text" class="admin-filter-bar__select" placeholder="Endpoint (e.g. rag)" [ngModel]="endpoint" (ngModelChange)="onEndpointChange($event)" />
        <select class="admin-filter-bar__select" [ngModel]="source" (ngModelChange)="onSourceChange($event)">
          <option value="">All sources</option>
          <option value="api">API</option>
          <option value="playground">Playground</option>
        </select>
        <select class="admin-filter-bar__select" [ngModel]="errorsOnly ? 'errors' : 'all'" (ngModelChange)="onErrorsOnlyChange($event)">
          <option value="all">All statuses</option>
          <option value="errors">Errors only</option>
        </select>
        <input type="date" class="admin-filter-bar__select" [ngModel]="from" (ngModelChange)="onFromChange($event)" title="From" />
        <input type="date" class="admin-filter-bar__select" [ngModel]="to" (ngModelChange)="onToChange($event)" title="To" />
      }
    </div>

    @if (apiKeyId) {
      <div class="admin-filter-bar admin-filter-bar--chip">
        <span class="txn-kind-badge txn-kind-badge--neutral">
          Filtered by API key
          <button type="button" class="admin-chip__clear" (click)="clearApiKeyFilter()" aria-label="Clear API key filter">×</button>
        </span>
      </div>
    }

    @if (isLoading) {
      <div class="admin-table-scroll">
        <table class="billing-table admin-stack admin-stack--calls">
          <thead>
            <tr>
              <th>Time</th>
              <th>Organization</th>
              <th>Source</th>
              <th>Endpoint</th>
              <th>Status</th>
              <th class="col-num">Cost</th>
              <th class="col-num">Latency</th>
              <th class="col-num">Credits</th>
            </tr>
          </thead>
          <tbody>
            @for (i of [1,2,3,4,5,6]; track i) {
              <tr>
                <td><app-skeleton width="100px" height="14px"></app-skeleton></td>
                <td><app-skeleton width="65%" height="14px"></app-skeleton></td>
                <td><app-skeleton width="50px" height="14px"></app-skeleton></td>
                <td><app-skeleton width="80%" height="14px"></app-skeleton></td>
                <td><app-skeleton width="40px" height="20px" radius="4px"></app-skeleton></td>
                <td><app-skeleton width="40px" height="14px"></app-skeleton></td>
                <td><app-skeleton width="50px" height="14px"></app-skeleton></td>
                <td><app-skeleton width="20px" height="14px"></app-skeleton></td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else if (rows.length) {
      <div class="admin-table-scroll">
        <table class="billing-table admin-stack admin-stack--calls">
          <thead>
            <tr>
              <th>Time</th>
              <th>Organization</th>
              <th>Source</th>
              <th>Endpoint</th>
              <th>Status</th>
              <th class="col-num">Cost</th>
              <th class="col-num">Latency</th>
              <th class="col-num">Credits</th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.id) {
              <tr>
                <td>{{ row.createdAt | date:'MMM d, y, HH:mm:ss' }}</td>
                <td><a [routerLink]="['/admin/organizations', row.organization.slug]" class="link">{{ row.organization.name }}</a></td>
                <td>{{ row.source }}</td>
                <td class="cell-endpoint"><span class="cell-endpoint__method">{{ row.method }}</span> /{{ row.endpoint }}</td>
                <td>
                  @if (row.error) {
                    <span class="txn-status-badge txn-status-badge--error">{{ row.status }}</span>
                  } @else {
                    <span class="cell-status--ok">{{ row.status }}</span>
                  }
                </td>
                <td class="col-num">
                  {{ row.costCents | money }}
                  @if (row.surchargeCents) {
                    <span class="cell-status--muted">({{ row.surchargeCents | money }} options)</span>
                  }
                </td>
                <td class="col-num" [class.cell-latency--slow]="isSlow(row)">{{ row.latencyMs | latency }}</td>
                <td class="col-num">{{ row.credits }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="table-footer">
        <span>{{ pagination?.total ?? rows.length | number }} call{{ (pagination?.total ?? rows.length) === 1 ? '' : 's' }}</span>
        @if (pagination && pagination.pages > 1) {
          <div class="table-footer__pager">
            <button type="button" class="btn--secondary" [disabled]="page <= 1" (click)="goToPage(page - 1)">Previous</button>
            <span>Page {{ page }} of {{ pagination.pages }}</span>
            <button type="button" class="btn--secondary" [disabled]="page >= pagination.pages" (click)="goToPage(page + 1)">Next</button>
          </div>
        }
      </div>
    } @else {
      <div class="empty-state">
        @if (hasFilters) {
          <p class="empty-state__title">No calls match your filters.</p>
          <p class="empty-state__desc">Try a different endpoint or organization, or clear the filters.</p>
          <button type="button" class="btn--secondary empty-state__action" (click)="clearFilters()">Clear filters</button>
        } @else {
          <p class="empty-state__title">No calls yet.</p>
          <p class="empty-state__desc">API calls appear here as soon as an organization starts using the API.</p>
        }
      </div>
    }
  </div>

</div>
